import { Result } from '@see-power-pft-common-js/types/result';
import {
  getErrorResult,
  getResult,
  getResultOr,
} from '@see-power-pft-common-js/utils/result/utils';
import { fetchWithAuth } from 'common-react/auth/fetch-with-auth';
import {
  AVAILABILITY_REPORT_DEFAULT_FILE_NAME,
  AVAILABILITY_REPORT_ENDPOINT_PATH,
  AVAILABILITY_REPORT_XLSX_CONTENT_TYPE,
} from 'constants/availability-report';
import {
  DownloadError,
  FetchBlobError,
  FetchError,
  IAvailabilityDownloadError,
  RequestUnauthorisedError,
  ResponseNotOkError,
} from 'pages/Availability/common/Modals/downloadData.errors';
import {
  DownloadedData,
  GenerateReportParams,
} from 'pages/Availability/common/Modals/downloadData.types';
import { downloadFile } from 'utils/download-file';

const getFileNameFromContentDisposition = (
  header: string | null,
): string | null => {
  if (!header) {
    return null;
  }

  const encodedMatch = /filename\*=(?:UTF-8'')?([^;\r\n]+)/i.exec(header);
  if (encodedMatch?.[1]) {
    const encoded = encodedMatch[1].trim().replace(/^["']|["']$/g, '');
    return decodeURIComponent(encoded);
  }

  const match = /filename=(?:"([^"]*)"|([^;\r\n]*))/i.exec(header);
  if (match?.[1]) {
    return match[1];
  }
  if (match?.[2]) {
    return match[2].trim();
  }

  return null;
};

export const fetchReportData = async ({
  getJwtToken,
  signalAuthError,
  siteSourceKey,
  fromDate,
  toDate,
}: GenerateReportParams): Promise<
  Result<IAvailabilityDownloadError, DownloadedData>
> => {
  const endpoint = import.meta.env.VITE_PLUTO_AVAILABILITY_API_ENDPOINT;

  const params = new URLSearchParams({
    siteSourceKey,
    fromDateUtc: fromDate.toISOString(),
    toDateUtc: toDate.toISOString(),
  });

  const fetchResult = await getResultOr(
    (cause) => new FetchError(cause),
    async () =>
      await fetchWithAuth(
        { getJwtToken, signalAuthError },
        `${endpoint}${AVAILABILITY_REPORT_ENDPOINT_PATH}?${params.toString()}`,
        {
          method: 'GET',
        },
      ),
  );

  const responseResult = fetchResult
    .flatMapAsync(async (response) => {
      if (response.status === 401) {
        return getErrorResult<IAvailabilityDownloadError>(
          new RequestUnauthorisedError({
            response: {
              status: response.status,
              statusText: response.statusText,
              headers: Array.from(response.headers?.entries() ?? []),
            },
          }),
        );
      }

      if (!response.ok) {
        let responseBody: string | undefined;
        try {
          responseBody = await response.clone().text();
        } catch {
          // Ignore errors reading body
        }
        return getErrorResult<IAvailabilityDownloadError>(
          new ResponseNotOkError({
            response: {
              status: response.status,
              statusText: response.statusText,
              headers: Array.from(response.headers?.entries() ?? []),
              bodyText: responseBody,
            },
          }),
        );
      }

      return getResult(response);
    })
    .then((it) =>
      it.flatMapAsync(async (response) => {
        const blobResult = await getResultOr(
          (cause) => new FetchBlobError(cause),
          async () => await response.blob(),
        );

        return blobResult.flatMap<IAvailabilityDownloadError, DownloadedData>(
          (blob) => {
            if (!blob || blob.size === 0) {
              const err = getErrorResult(
                new FetchBlobError(new Error('Blob is empty')),
              );
              if (!err.success) {
                return err.asLeftOf<DownloadedData>();
              }
            }

            if (blob.type !== AVAILABILITY_REPORT_XLSX_CONTENT_TYPE) {
              const err = getErrorResult(
                new DownloadError(
                  `Invalid report format. Expected Excel file, got ${blob.type}`,
                ),
              );
              if (!err.success) {
                return err.asLeftOf<DownloadedData>();
              }
            }

            const fileName =
              getFileNameFromContentDisposition(
                response.headers.get('content-disposition'),
              ) ?? AVAILABILITY_REPORT_DEFAULT_FILE_NAME;

            return getResult({ blob, fileName });
          },
        );
      }),
    );

  return responseResult;
};

export const downloadData = async (
  params: GenerateReportParams,
): Promise<Result<IAvailabilityDownloadError, void>> =>
  (await fetchReportData(params)).map(({ blob, fileName }) =>
    downloadFile(blob, fileName),
  );
